import { COLLECTION_COLORS, scratchCollection, type ColorName, type NewView, type ViewType } from '@tomo/core';
import { Button, Dialog, Field, Input } from '@tomo/ui';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useCollectionGroup, useCollections, useProject, useSession } from '../../data/hooks.tsx';
import { useStore } from '../../lib/store.ts';
import { IconPicker } from '../shell/IconPicker.tsx';
import { ProjectTile } from '../shell/ProjectTile.tsx';
import { closeNewCollection, newCollectionOpen, openNewCollection } from '../shell/ui-state.ts';
import { VIEW_TYPE_LABEL } from './view-utils.ts';
import { VIEW_ICON } from './ViewToolbar.tsx';
import './new-collection.css';

const VIEW_ORDER: ViewType[] = ['table', 'board', 'gallery', 'list'];
const VIEW_HINT: Record<ViewType, string> = {
  table: 'Rows and columns. Best for stats and numbers.',
  board: 'Cards in columns. Adds a Status field to sort them into.',
  gallery: 'Cards with a picture on top.',
  list: 'Titles with their first line of text, like notes.',
};

/**
 * Every collection starts from scratch: each game is different, so there are no ready-made
 * schemas, and every collection behaves the same. Pick a name, an icon and the views; fields are
 * added in the collection itself, and every record can carry a cover picture without one.
 */
export function NewCollectionDialog() {
  const { open, groupId } = useStore(newCollectionOpen);
  const [params, setParams] = useSearchParams();
  const project = useProject();
  const session = useSession();
  const collections = useCollections();
  const group = useCollectionGroup(groupId);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('i:box');
  const [color, setColor] = useState<ColorName>('orange');
  const [views, setViews] = useState<ViewType[]>([]);

  // `?newCollection=1` (from the command palette) opens the dialog too.
  useEffect(() => {
    if (params.get('newCollection') === '1') {
      openNewCollection();
      params.delete('newCollection');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    if (!open) return;
    setName('');
    setIcon('i:box');
    setColor(COLLECTION_COLORS[collections.length % COLLECTION_COLORS.length]!);
    setViews([]);
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => closeNewCollection();
  const ready = name.trim() !== '' && views.length > 0;

  const toggleView = (type: ViewType) =>
    setViews((current) => (current.includes(type) ? current.filter((t) => t !== type) : VIEW_ORDER.filter((t) => t === type || current.includes(t))));

  const create = () => {
    if (!ready) return;
    // `scratchCollection` names the record after the collection and adds a Status for a board.
    const newViews: NewView[] = views.map((type) => ({ name: VIEW_TYPE_LABEL[type], type }));
    const id = project.createCollection(scratchCollection(name, { icon, color, groupId, views: newViews }));
    close();
    navigate(`/p/${session.id}/c/${id}`);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title="New collection"
      description={
        group ? `In ${group.name}. It starts empty: add the fields your game needs once it’s created.` : 'It starts empty: add the fields your game needs once it’s created.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={create} disabled={!ready} shortcut="mod+enter">
            Create collection
          </Button>
        </>
      }
    >
      <form
        className="new-collection"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) create();
        }}
      >
        <div className="new-collection__identity">
          <IconPicker
            value={icon}
            onChange={(t) => t && setIcon(t)}
            color={color}
            onColorChange={setColor}
            trigger={
              <button type="button" className="new-collection__icon" aria-label="Choose icon and color">
                <ProjectTile icon={icon} color={color} size={52} />
              </button>
            }
          />
          <div className="new-collection__fields">
            <Field label="Name" htmlFor="nc-name">
              <Input id="nc-name" size="lg" autoFocus placeholder="e.g. Recipes" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
        </div>

        <div>
          <div className="new-collection__label">
            <span className="g-overline">Views</span>
            <span className="new-collection__hint">{views.length ? `${views.length} selected` : 'Pick at least one'}</span>
          </div>
          <div className="view-choices" role="group" aria-label="Views">
            {VIEW_ORDER.map((type) => {
              const on = views.includes(type);
              return (
                <button key={type} type="button" role="checkbox" aria-checked={on} className="view-choice" onClick={() => toggleView(type)}>
                  <span className="view-choice__head">
                    <span className="view-choice__icon">{VIEW_ICON[type]}</span>
                    <span className="view-choice__name">{VIEW_TYPE_LABEL[type]}</span>
                    <span className="view-choice__check">{on && <Check size={12} strokeWidth={3} />}</span>
                  </span>
                  <span className="view-choice__hint">{VIEW_HINT[type]}</span>
                </button>
              );
            })}
          </div>
        </div>
      </form>
    </Dialog>
  );
}
